import {
  Box,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import { tokens } from "../../../theme";
import { CalendarIcon, LocationIcon } from "../../../components/icons";

interface ConferenceBroadcastPanelProps {
  conference: {
    title: string;
    theme: string;
    date: string;
    endDate?: string | null;
    time?: string | null;
    attendees: number;
    localImpact: string;
    categories: string[];
  };
  venueName: string;
}

export default function ConferenceBroadcastPanel({
  conference,
  venueName,
}: ConferenceBroadcastPanelProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const cardBg = colors.primary[900];
  const mutedCard = colors.primary[800];
  const textPrimary = colors.primary[700];
  const textMuted = colors.primary[300];

  const formatEventDate = (value: string) =>
    new Date(value).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).replace(/,/g, "");

  const formatEventTime = (value: string) =>
    new Date(`1970-01-01T${value}`).toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const dateRangeLabel =
    (conference.endDate
      ? `${formatEventDate(conference.date)} – ${formatEventDate(conference.endDate)}`
      : formatEventDate(conference.date)) +
    (conference.time ? ` at ${formatEventTime(conference.time)}` : "");

  return (
    <Card elevation={0} sx={{ bgcolor: cardBg, borderRadius: "18px" }}>
      <Box sx={{ p: 2.5 }}>
        <Box
          sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              bgcolor: "#3B82F622",
              color: "#3B82F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LiveTvOutlinedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: 19,
                fontWeight: 700,
                color: textPrimary,
                lineHeight: 1.25,
              }}
            >
              {conference.title}
            </Typography>
            <Stack
              direction="row"
              spacing={0.75}
              mt={0.75}
              flexWrap="wrap"
              gap={0.5}
            >
              {conference.categories.map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  size="small"
                  sx={{
                    fontSize: 11,
                    height: 22,
                    bgcolor: colors.primary[200] || "#D1A07722",
                    color: colors.primary[400],
                    borderRadius: "6px",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ my: 2, borderColor: `${textMuted}18` }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
            mb: 2,
          }}
        >
          {[
            {
              icon: <CalendarIcon sx={{ fontSize: 24 }} />,
              label: conference.endDate ? "Dates" : "Start Date",
              value: dateRangeLabel,
            },
            {
              icon: <PeopleOutlineIcon sx={{ fontSize: 24 }} />,
              label: "Expected Attendees",
              value: conference.attendees.toLocaleString(),
            },
            {
              icon: <LocationIcon sx={{ fontSize: 24 }} />,
              label: "Venue",
              value: venueName,
            },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{ bgcolor: mutedCard, borderRadius: "12px", p: 1.75 }}
            >
              <Box sx={{ color: textMuted, mb: 0.75 }}>{item.icon}</Box>
              <Typography
                sx={{
                  fontSize: 10,
                  color: textMuted,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  fontWeight: 600,
                  mb: 0.4,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: textPrimary,
                  lineHeight: 1.3,
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ bgcolor: mutedCard, borderRadius: "12px", p: 1.75, mb: 2 }}>
          <Typography
            sx={{
              fontSize: 10,
              color: textMuted,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontWeight: 600,
              mb: 0.75,
            }}
          >
            Event Theme
          </Typography>
          <Typography
            sx={{ fontSize: 14, color: textPrimary, lineHeight: 1.6 }}
          >
            {conference.theme}
          </Typography>
        </Box>

        <Box sx={{ bgcolor: mutedCard, borderRadius: "12px", p: 1.75 }}>
          <Typography
            sx={{
              fontSize: 10,
              color: textMuted,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontWeight: 600,
              mb: 0.75,
            }}
          >
            Broadcast Impact
          </Typography>
          <Typography
            sx={{ fontSize: 14, color: textPrimary, lineHeight: 1.6 }}
          >
            {conference.localImpact || "Not provided"}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}
